/**
 * Frais partagés — logique pure (Phase 1, incrément 16).
 *
 * Port du calcul de solde de `vCpFrais` (proto l. 2960-2967) en
 * helpers testables, sans accès au DOM ni au store. Garde-fou
 * critique (CLAUDE.md §2.3) : « Frais partagés : calcul automatique
 * du solde "X doit Y € à Z" en temps réel. »
 *
 * Algorithme :
 *   - `totalA` = somme des dépenses payées par le parent A
 *   - `totalB` = somme des dépenses payées par le parent B
 *   - `total` = totalA + totalB
 *   - `halfTotal` = total / 2
 *   - `balanceA` = totalA − halfTotal
 *     - > 0  → B doit `balanceA` € à A (A a payé plus que sa moitié)
 *     - < 0  → A doit `|balanceA|` € à B
 *     - |x| < 0.01 → équilibré (tolérance flottants centimes)
 */
import type { Expense, ExpenseCategory } from '@nestor/shared-types';
import type { IconName } from '../../ui/icons.js';

/** Tolérance d'égalité au centime (évite les artefacts flottants). */
export const BALANCE_EPSILON = 0.01;

export interface BalanceResult {
  /** Total payé par le parent A (en €). */
  totalA: number;
  /** Total payé par le parent B (en €). */
  totalB: number;
  /** Somme totale des dépenses (totalA + totalB). */
  total: number;
  /** Vrai si la balance est < `BALANCE_EPSILON`. */
  balanced: boolean;
  /** Parent qui DOIT, `null` si balanced. */
  from: 'A' | 'B' | null;
  /** Parent à QUI on doit, `null` si balanced. */
  to: 'A' | 'B' | null;
  /** Montant de la dette (toujours ≥ 0, en €). */
  amount: number;
}

/**
 * Calcule la balance entre les 2 parents à partir de la liste de
 * dépenses partagées. Pure — pas de side effects.
 */
export function computeBalance(expenses: readonly Expense[]): BalanceResult {
  let totalA = 0;
  let totalB = 0;
  for (const e of expenses) {
    if (!Number.isFinite(e.amount)) continue;
    if (e.paidBy === 'A') totalA += e.amount;
    else if (e.paidBy === 'B') totalB += e.amount;
  }
  const total = totalA + totalB;
  const halfTotal = total / 2;
  const balanceA = totalA - halfTotal;
  const balanced = Math.abs(balanceA) < BALANCE_EPSILON;
  if (balanced) {
    return { totalA, totalB, total, balanced: true, from: null, to: null, amount: 0 };
  }
  if (balanceA > 0) {
    // A a payé plus → B doit le surplus à A
    return {
      totalA,
      totalB,
      total,
      balanced: false,
      from: 'B',
      to: 'A',
      amount: Math.abs(balanceA),
    };
  }
  return {
    totalA,
    totalB,
    total,
    balanced: false,
    from: 'A',
    to: 'B',
    amount: Math.abs(balanceA),
  };
}

/**
 * Formate un montant en euros selon la locale (audit C) : « 30,00 € »
 * en FR (virgule + espace insécable), « €30.00 » en EN. `lang` est
 * passé par l'appelant (typiquement `getLang()`) — défaut `fr` pour
 * préserver la pureté du module (aucun import de store).
 */
export function formatAmount(amount: number, lang = 'fr'): string {
  const safe = Number.isFinite(amount) ? amount : 0;
  return new Intl.NumberFormat(lang, { style: 'currency', currency: 'EUR' }).format(safe);
}

/**
 * Icône filaire par catégorie de dépense (audit D2 — zéro emoji UI).
 * Le module reste pur : on mappe vers des NOMS d'icônes (`IconName`),
 * les vues rendent `Icon(Icons[name])`.
 */
export const EXPENSE_CATEGORY_ICONS: Record<ExpenseCategory, IconName> = {
  santé: 'health',
  éducation: 'book',
  vêtements: 'shirt',
  loisirs: 'ball',
  transport: 'car',
  autre: 'euro',
};
